import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Pencil } from "lucide-react";
import { ThemedLogo } from "@/components/shared/ThemedLogo";

interface OnboardingWelcomeProps {
  defaultGoal: string;
  onContinue: (goal: string) => void;
}

const STARTER_GOALS = [
  {
    label: "Build a daily summary of my email",
    goal: "I want to create a daily summary of my email that outlines any emails I haven't responded too, provides a quick summary and pulls out any next steps.",
  },
  {
    label: "Create a competitive analysis for a client",
    goal: "Help me create an in-depth competitive analysis for one of my clients",
  },
  {
    label: "Analyze the vibe of my video call background",
    goal: "Take a look at my current video call background and summarize the vibe while making recommendations on how to make it more interesting.",
  },
];

export function OnboardingWelcome({ defaultGoal, onContinue }: OnboardingWelcomeProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customGoal, setCustomGoal] = useState("");
  const [isCustom, setIsCustom] = useState(false);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setIsCustom(false);
  };

  const handleSelectCustom = () => {
    setIsCustom(true);
    setSelectedIndex(-1);
  };

  const handleContinue = () => {
    const goal = isCustom ? customGoal.trim() : STARTER_GOALS[selectedIndex].goal;
    if (goal) {
      onContinue(goal);
    }
  };

  const canContinue = isCustom ? customGoal.trim().length > 0 : selectedIndex >= 0;

  return (
    <Card className="border-primary/20 bg-card/80 backdrop-blur">
      <CardHeader className="text-center space-y-3">
        <div className="flex justify-center">
          <ThemedLogo className="h-24 w-auto" />
        </div>
        <CardTitle className="text-3xl font-bold">Welcome! Let's build a prompt!</CardTitle>
        <CardDescription className="text-base text-muted-foreground max-w-2xl mx-auto">
          Cosmo's Build-a-Prompt tool will turn a simple idea into a powerful prompt. You can select one of the options below or write your own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preset goal options */}
        <div className="space-y-3">
          {STARTER_GOALS.map((item, index) => (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              className={`w-full text-left p-4 rounded-xl border transition-colors text-sm leading-relaxed ${
                selectedIndex === index && !isCustom
                  ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                  : "border-border hover:border-primary/50 hover:bg-muted/30"
              }`}
            >
              {item.label}
            </button>
          ))}

          {/* Write my own - 4th card */}
          <button
            onClick={handleSelectCustom}
            className={`w-full text-left p-4 rounded-xl border transition-colors text-sm leading-relaxed ${
              isCustom
                ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            }`}
          >
            {isCustom ? (
              <textarea
                value={customGoal}
                onChange={(e) => setCustomGoal(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="What do you want the AI to help you with?"
                autoFocus
                rows={3}
                className="w-full resize-none bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
              />
            ) : (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Pencil className="h-4 w-4" />
                Write my own
              </span>
            )}
          </button>
        </div>

        <Button
          onClick={handleContinue}
          className="w-full"
          size="lg"
          disabled={!canContinue}
        >
          Start Building
        </Button>
      </CardContent>
    </Card>
  );
}
